import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useConvex, usePaginatedQuery, useQuery } from "convex/react";
import { canonicalJson } from "@anthemreactv2/contracts";
import { api } from "@anthemreactv2/backend/labs-api";
import type { FunctionReturnType } from "convex/server";
import type { Doc, Id } from "@anthemreactv2/backend/convex/_generated/dataModel";
import { Archive, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CloudGate, message } from "./cloud";
import { PageHeading, Panel, Notice, SelectField } from "./components/workbench";
import { TrainingProductionPlan } from "./TrainingProductionPlan";
import { BlendRecipeEditor, type BlendChoices } from "./BlendRecipeEditor";
import { draftFromVersion, emptyBlend, recipeFromDraft, type BlendDraft } from "./blend-editor";

type BlendDetail = FunctionReturnType<typeof api.labs.blends.get>;
type Transition = (action: () => void) => void;

/** Load every page before search, so an older coffee/profile is not silently omitted. */
function useAllPages<T>(page: { results: T[]; status: string; loadMore: (count: number) => void }) {
  useEffect(() => { if (page.status === "CanLoadMore") page.loadMore(50); }, [page.status, page.loadMore]);
  return page.results;
}
export function ProductionPlan({ onDirtyChange, onBusyChange }: {
  onDirtyChange: (value: boolean) => void; onBusyChange: (value: boolean) => void;
}) {
  const [training, setTraining] = useState(false), [dirty, setDirty] = useState(false), [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  useEffect(() => () => { onDirtyChange(false); onBusyChange(false); }, [onDirtyChange, onBusyChange]);
  const request: Transition = (action) => {
    if (busy) return;
    if (dirty) setPending(() => action);
    else action();
  };
  return <>
    <PageHeading title="Blends & production planning" description="Create and edit your recipes, then preview the coffee you need."
      actions={<Button variant="outline" className="min-h-11" disabled={busy} onClick={() => request(() => setTraining(!training))}>
        {training ? "Back to saved blends" : "Try sample blends"}
      </Button>} />
    {training ? <TrainingProductionPlan /> : <CloudGate><BlendHome request={request} dirty={dirty} busy={busy} onDirtyChange={setDirty} onBusyChange={setBusy} /></CloudGate>}
    <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Discard unsaved recipe changes?</AlertDialogTitle>
          <AlertDialogDescription>Save your blend before switching if you want to keep these edits. Your last saved version will stay available.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="min-h-11">Keep editing</AlertDialogCancel><AlertDialogAction className="min-h-11" disabled={busy} onClick={() => {
          const action = pending; setPending(null); setDirty(false); action?.();
        }}>Discard changes</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}
function BlendHome(props: { request: Transition; dirty: boolean; busy: boolean; onDirtyChange: (value: boolean) => void; onBusyChange: (value: boolean) => void }) {
  const mine = useQuery(api.labs.roasteries.mine, {});
  const [selected, setSelected] = useState("");
  if (!mine) return <p role="status">Loading your roasteries…</p>;
  const roastery = selected ? mine.roasteries.find((item) => item._id === selected) : mine.roasteries[0];
  if (!roastery) return <Panel title="Start with a roastery" description="Recipes are shared within a roastery.">
    <p className="text-sm text-muted-foreground">Create a roastery in Roastery operations. You can add manual coffee names here without receiving stock first.</p>
    <Button asChild className="min-h-11"><Link to="/roasting/operations">Open roastery operations</Link></Button>
  </Panel>;
  return <>
    <SelectField label="Roastery" value={roastery._id} className="max-w-sm [&_select]:min-h-11" disabled={props.busy}
      onChange={(event) => { const id = event.target.value; props.request(() => setSelected(id)); }}>
      {mine.roasteries.map((item) => <option key={item._id} value={item._id}>{item.name}</option>)}
    </SelectField>
    <BlendLibrary key={roastery._id} roasteryId={roastery._id} {...props} />
  </>;
}
function BlendLibrary({ roasteryId, request, dirty, busy, onDirtyChange, onBusyChange }: {
  roasteryId: Id<"labsRoasteries">; request: Transition; dirty: boolean; busy: boolean;
  onDirtyChange: (value: boolean) => void; onBusyChange: (value: boolean) => void;
}) {
  const client = useConvex();
  const [archived, setArchived] = useState(false);
  const [selection, setSelection] = useState<{ blendId?: Id<"labsBlends">; versionId?: Id<"labsBlendVersions">; copy?: BlendDraft; session: number }>({ session: 0 });
  const [announcement, setAnnouncement] = useState(""), [error, setError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Doc<"labsBlends"> | null>(null);
  const archiveTicket = useRef<{ payload: string; key: string } | null>(null), archiveFlight = useRef(false);
  const blendsPage = usePaginatedQuery(api.labs.blends.list, { roasteryId, archived }, { initialNumItems: 50 });
  const lotsPage = usePaginatedQuery(api.labs.inventory.lots, { roasteryId }, { initialNumItems: 50 });
  const coffeesPage = usePaginatedQuery(api.labs.blends.coffees, { roasteryId }, { initialNumItems: 50 });
  const profilesPage = usePaginatedQuery(api.labs.recordings.profiles, { roasteryId }, { initialNumItems: 50 });
  const versionsPage = usePaginatedQuery(api.labs.blends.versions, selection.blendId ? { roasteryId, blendId: selection.blendId } : "skip", { initialNumItems: 50 });
  const blends = useAllPages(blendsPage), lots = useAllPages(lotsPage), coffees = useAllPages(coffeesPage), profiles = useAllPages(profilesPage), versions = useAllPages(versionsPage);
  const machines = useQuery(api.labs.roasteries.machines, { roasteryId });
  const detail = useQuery(api.labs.blends.get, selection.blendId ? { roasteryId, blendId: selection.blendId, versionId: selection.versionId } : "skip");
  useEffect(() => {
    if (!detail || selection.versionId || !selection.blendId) return;
    // Freeze the displayed version as well as the form when another operator saves.
    setSelection((current) => current.session === selection.session && !current.versionId
      ? { ...current, versionId: detail.version._id } : current);
  }, [detail, selection.blendId, selection.versionId, selection.session]);
  const optionsLoading = [lotsPage, coffeesPage, profilesPage].some((item) => item.status !== "Exhausted") || machines === undefined;
  const profileChoices = profiles.filter((profile) => profile.provenance !== "synthetic").map((profile) => ({
    value: profile.latestVersionId, name: profile.name, revision: profile.revision, machineId: profile.machineId,
  }));
  for (const profile of detail?.profileVersions ?? []) {
    if (!profileChoices.some((choice) => choice.value === profile._id)) profileChoices.push({
      value: profile._id, name: profile.name, revision: profile.revision, machineId: profile.machineId,
    });
  }
  const choices: BlendChoices = {
    coffees: [
      ...lots.map((lot) => ({ value: `lot:${lot._id}`, label: lot.name, sublabel: `${lot.physicalLotCode} · physical green lot`, searchText: `${lot.supplier} ${lot.location}` })),
      ...coffees.map((coffee) => ({ value: `manual:${coffee._id}`, label: coffee.name, sublabel: "Manual coffee · stock not linked" })),
    ].sort((a, b) => a.label.localeCompare(b.label)),
    lots, machines: machines ?? [], profiles: profileChoices, loading: optionsLoading,
  };
  function open(next: Omit<typeof selection, "session">) {
    setSelection((current) => ({ ...next, session: current.session + 1 })); setError(null); setAnnouncement("");
  }
  async function archive(blend: Doc<"labsBlends">) {
    if (archiveFlight.current) return;
    const args = { roasteryId, blendId: blend._id, expectedRevision: blend.revision, expectedArchived: blend.archived, archived: !blend.archived };
    const payload = canonicalJson(args);
    if (archiveTicket.current?.payload !== payload) archiveTicket.current = { payload, key: crypto.randomUUID() };
    archiveFlight.current = true; onBusyChange(true); setError(null);
    try {
      await client.mutation(api.labs.blends.setArchived, { ...args, operationKey: archiveTicket.current.key });
      archiveTicket.current = null; setArchiveTarget(null); setArchived(args.archived); open({ blendId: args.blendId });
      setAnnouncement(`${blend.name} ${args.expectedArchived ? "restored" : "archived"}. Its saved versions are kept.`);
    } catch (cause) { setError(message(cause)); }
    finally { archiveFlight.current = false; onBusyChange(false); }
  }
  return <>
    <Panel title="Your blends" description="Choose a saved blend to edit, or start a new recipe."
      action={<Button className="min-h-11" disabled={busy} onClick={() => request(() => { setArchived(false); open({}); })}><Plus aria-hidden="true" />New blend</Button>}>
      <div className="grid min-w-0 items-start gap-4 sm:grid-cols-[minmax(0,1fr)_11rem]">
        <div className="min-w-0 space-y-2">
          <Label htmlFor="saved-blend-picker">Blend recipe</Label>
          <ComboboxSelect id="saved-blend-picker" value={selection.blendId ?? ""}
            options={blends.map((blend) => ({ value: blend._id, label: blend.name, sublabel: `Version ${blend.revision}${blend.archived ? " · archived" : ""}` }))}
            loading={blendsPage.status !== "Exhausted"} disabled={busy || blendsPage.status !== "Exhausted"}
            placeholder={blendsPage.status !== "Exhausted" ? "Loading blends…" : "Choose a saved blend"}
            searchPlaceholder="Search your blends…" emptyText={archived ? "No archived blends." : "No saved blends. Start with New blend."}
            className="h-auto min-h-11" contentClassName="[&_[cmdk-item]]:min-h-11 [&_[cmdk-input]]:h-11 [&_[data-slot=command-input-wrapper]]:h-11" onValueChange={(id) => { if (id) request(() => open({ blendId: id as Id<"labsBlends"> })); }} />
        </div>
        <SelectField label="Show recipes" value={archived ? "archived" : "active"} disabled={busy} className="[&_select]:min-h-11"
          onChange={(event) => { const value = event.target.value === "archived"; request(() => { setArchived(value); open({}); }); }}>
          <option value="active">Active blends</option><option value="archived">Archived blends</option>
        </SelectField>
      </div>
      {blendsPage.status === "Exhausted" && blends.length === 0 && <p className="text-sm text-muted-foreground">{archived ? "No archived recipes yet." : "No blends saved yet. Your first recipe can use physical lots, manual coffee names, or both."}</p>}
      {detail && <div className="flex flex-wrap items-center gap-2 border-t pt-4">
        <SelectField label="Recipe version" value={selection.versionId ?? detail.version._id} disabled={busy || versionsPage.status !== "Exhausted"}
          className="w-full sm:w-auto [&_select]:min-h-11" onChange={(event) => { const id = event.target.value; request(() => open({ blendId: detail.blend._id, versionId: id as Id<"labsBlendVersions"> })); }}>
          {versions.map((version) => <option key={version._id} value={version._id}>Version {version.revision}{version.revision === detail.blend.revision ? " · latest" : ""}</option>)}
        </SelectField>
        <Button type="button" variant="outline" className="min-h-11" disabled={busy}
          onClick={() => request(() => open({ blendId: detail.blend._id }))}><RotateCcw aria-hidden="true" />Reload latest</Button>
        <Button type="button" variant="outline" className="min-h-11" disabled={busy} onClick={() => request(() => {
          const copy = draftFromVersion(detail.version); copy.name = ""; setArchived(false); open({ copy });
        })}>Copy as new blend</Button>
        <Button type="button" variant="ghost" className="min-h-11" disabled={busy || dirty} onClick={() => {
          if (detail.blend.archived) void archive(detail.blend); else { setError(null); setArchiveTarget(detail.blend); }
        }}><Archive aria-hidden="true" />{detail.blend.archived ? "Restore blend" : "Archive blend"}</Button>
        {dirty && <p className="text-xs text-muted-foreground">Save or reload before archiving.</p>}
      </div>}
    </Panel>
    {announcement && <p role="status" className="text-sm">{announcement}</p>}
    {error && <Notice error>{error}</Notice>}
    {selection.blendId && !detail ? <p role="status">Loading your recipe…</p> : <EditorSession key={selection.session}
      roasteryId={roasteryId} detail={detail} copy={selection.copy} choices={choices} onDirtyChange={onDirtyChange} onBusyChange={onBusyChange}
      onSaved={(id, name) => { setArchived(false); open({ blendId: id }); setAnnouncement(`${name} saved. This recipe is shared with your roastery.`); }} />}
    <AlertDialog open={archiveTarget !== null} onOpenChange={(value) => { if (!value && !archiveFlight.current) setArchiveTarget(null); }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Archive {archiveTarget?.name}?</AlertDialogTitle>
        <AlertDialogDescription>This hides the blend from active recipes. Its saved versions stay available, and you can restore it from Archived blends.</AlertDialogDescription></AlertDialogHeader>
        {error && <Notice error>{error}</Notice>}
        <AlertDialogFooter><AlertDialogCancel className="min-h-11" disabled={busy}>Keep blend</AlertDialogCancel>
          <AlertDialogAction className="min-h-11" disabled={busy} onClick={(event) => { event.preventDefault(); if (archiveTarget) void archive(archiveTarget); }}>{busy ? "Archiving…" : "Archive blend"}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
    </AlertDialog>
  </>;
}
function EditorSession({ roasteryId, detail, copy, choices, onSaved, onDirtyChange, onBusyChange }: {
  roasteryId: Id<"labsRoasteries">; detail: BlendDetail | undefined; copy?: BlendDraft; choices: BlendChoices;
  onSaved: (id: Id<"labsBlends">, name: string) => void; onDirtyChange: (value: boolean) => void; onBusyChange: (value: boolean) => void;
}) {
  const client = useConvex();
  const [initial] = useState(() => copy ?? (detail ? draftFromVersion(detail.version) : emptyBlend()));
  const [openedRevision] = useState(detail?.blend.revision ?? 0);
  const [savedRevision] = useState(detail?.version.revision);
  return <>
    {savedRevision !== undefined && savedRevision < openedRevision && <Notice title={`Editing saved version ${savedRevision}`}>
      Saving creates version {openedRevision + 1}. All previous recipe versions stay available.
    </Notice>}
    <BlendRecipeEditor initial={initial} choices={choices} savedRevision={savedRevision} openedRevision={openedRevision}
      currentRevision={detail?.blend.revision} readOnly={detail?.blend.archived} onDirtyChange={onDirtyChange} onBusyChange={onBusyChange}
      onAddCoffee={async (name, operationKey) => {
        const id = await client.mutation(api.labs.blends.addCoffee, { roasteryId, name, operationKey });
        return { value: `manual:${id}`, label: name.trim().replace(/\s+/g, " "), sublabel: "Manual coffee · stock not linked" };
      }} onSave={async (draft, operationKey) => {
        const id = await client.mutation(api.labs.blends.save, {
          roasteryId, blendId: detail?.blend._id, expectedRevision: openedRevision,
          name: draft.name, notes: draft.notes, recipe: recipeFromDraft(draft), operationKey,
        });
        onSaved(id, draft.name.trim());
      }} />
  </>;
}
